class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get headerNavigation() {
    return this.querySelector('.header__bottom');
  }

  get navigationToggleButton() {
    return this.querySelector('.toggle-navigation-button');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    // window.addEventListener('resize', this.setMenuHeight.bind(this));

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    // setHeight() must run at first layout: --header-height drives the sticky
    // offsets. It used to be deferred behind delayUntilInteraction (a 5s
    // setTimeout), which left those offsets wrong until the timer fired.
    this.setHeight();

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      // Both reads happen before either write. Writing a custom property on
      // documentElement invalidates style for the whole document, so a read
      // placed after one forces a full synchronous layout — that interleaving
      // was costing ~139ms of forced reflow per run, and this runs on every
      // ResizeObserver callback.
      const offsetHeight = Math.round(this.offsetHeight);
      // header__bottom is only rendered when section.settings.menu is set.
      const offsetNavigationHeight = Math.round(this.headerNavigation?.offsetHeight || 0);

      // Only these two are written: they are the only header metrics anything
      // reads. --header-height feeds the sticky `top:` calcs in theme.css and
      // --header-navigation-height the nav offsets. Neither affects the
      // header's own box, so the ResizeObserver above cannot re-trigger itself
      // from these writes — keep it that way if you add another property here.
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty(
        '--header-navigation-height',
        `${Math.max(offsetNavigationHeight - 1, 0)}px`
      );
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    // Group CSS classes in one object for easier management
    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    // Track navigation toggle state
    this.navigationManuallyToggled = false;

    // Initialize scroll tracking variables
    this.currentScrollTop = 0;
    this.lastScrollPos = 0;

    // Add scroll threshold to prevent quick unpinning
    this.scrollThreshold = 200; // Minimum scroll amount before unpinning
    this.scrollDirection = 'none';
    this.scrollDistance = 0;
  }

  // Getters for easier property access
  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get collapseOnScroll() {
    return this.dataset.collapseOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    // Store initial scroll position
    this.firstScrollTop = window.scrollY;

    // Cache header dimensions for performance optimization
    this.headerBounds = this.headerSection.getBoundingClientRect();

    // Same idea for the home-hero boundary, which handleScroll consults on
    // every frame. See refreshHomeHeroBoundary().
    this.isIndexTemplate = document.body.classList.contains('index-template');
    this.homeHeroBoundary = null;
    if (this.isIndexTemplate) {
      this.refreshHomeHeroBoundary();

      // offsetTop moves as images above the section load and as the slideshow
      // settles, so re-measure whenever the page actually changes size. A
      // ResizeObserver callback runs after layout, so this read is free —
      // unlike the per-frame read it replaces.
      window.addEventListener('load', () => this.refreshHomeHeroBoundary(), { once: true });
      if (window.ResizeObserver) {
        this.homeHeroObserver = new ResizeObserver(() => this.refreshHomeHeroBoundary());
        this.homeHeroObserver.observe(document.body);
      }
    }

    // Initialize sticky header
    this.initStickyHeader();

    // Register toggle button event if needed
    if (this.collapseOnScroll && this.navigationToggleButton) {
      this.navigationToggleButton.addEventListener('click', this.handleNavigationToggle.bind(this));
    }
  }

  disconnectedCallback() {
    // The theme editor tears this section down and rebuilds it on every edit,
    // so the body observer above has to go with it.
    this.homeHeroObserver?.disconnect();
    this.homeHeroObserver = null;
  }

  // Initialize sticky header behavior
  initStickyHeader() {
    this.headerSection.classList.add(this.classes.headerSticky);
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }

  // Handle navigation toggle button click
  handleNavigationToggle(event) {
    event.preventDefault();

    this.navigationManuallyToggled = true;
    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    // Toggle visibility state
    setTimeout(
      () => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      },
      isHidden ? 0 : 250
    );
    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);
    this.navigationToggleButton.setAttribute('aria-expanded', isHidden);

    // Reset toggle state after delay
    setTimeout(() => {
      this.navigationManuallyToggled = false;
    }, 1000);
  }

  // Handle scroll events
  handleScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid recalculating dimensions on each scroll
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Update scroll direction and distance
    this.updateScrollMetrics(scrollTop);

    // Use requestAnimationFrame for performance optimization
    requestAnimationFrame(() => {
      const isScrolledPastHeader = scrollTop > headerBoundsTop;

      // Handle different scroll positions
      if (isScrolledPastHeader) {
        this.handleScrolledPastHeader(scrollTop, headerBoundsBottom);
      } else {
        this.handleScrolledBeforeHeader();
      }

      this.currentScrollTop = scrollTop;
    });
  }

  // Update scroll direction and accumulated distance
  updateScrollMetrics(scrollTop) {
    // Determine scroll direction
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';

    // Reset accumulated distance when direction changes
    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      // Accumulate scroll distance in the same direction
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  // Handle scroll position after passing the header
  handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    this.headerSection.classList.add(this.classes.headerScrolled);
    const keepHeaderPinnedForHomeHero = this.shouldKeepHeaderPinnedForHomeHero(scrollTop);

    // Handle collapse on scroll behavior
    if (this.collapseOnScroll) {
      this.navigationToggleButton.classList.add(this.classes.show);
      if (keepHeaderPinnedForHomeHero && !this.navigationManuallyToggled) {
        document.body.classList.remove(this.classes.isHideNav);
        this.headerNavigation.classList.remove(this.classes.hide);
        this.headerNavigation.classList.remove(this.classes.show);
        this.navigationToggleButton.setAttribute('aria-expanded', false);
      } else if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.add(this.classes.hide);
        document.body.classList.add(this.classes.isHideNav);
      }
    }

    if (keepHeaderPinnedForHomeHero) {
      document.body.classList.add(this.classes.pinned);
      return;
    }

    // Handle sticky behavior based on configuration
    if (this.isAlwaysSticky) {
      document.body.classList.add(this.classes.pinned);
    } else {
      // Improved sticky on scroll up logic
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;

      if (isScrollingUp || isNearHeader) {
        document.body.classList.add(this.classes.pinned);
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        // Only unpin when scrolled down enough and not manually toggled
        document.body.classList.remove(this.classes.pinned);
      }
    }
  }

  refreshHomeHeroBoundary() {
    const collectionSection = document.querySelector(
      '#MainContent > .shopify-section[id*="collection_list_8hUigt"]'
    );

    this.homeHeroBoundary = collectionSection ? collectionSection.offsetTop : null;
  }

  shouldKeepHeaderPinnedForHomeHero(scrollTop) {
    // Deliberately just a number comparison. This is called from inside the
    // scroll rAF, straight after handleScrolledPastHeader() writes a class on
    // the header — so the querySelector and offsetTop read that used to live
    // here forced a full synchronous layout on every single scroll frame.
    if (this.homeHeroBoundary === null) return false;

    return scrollTop < this.homeHeroBoundary - 2;
  }

  // Handle scroll position before the header
  handleScrolledBeforeHeader() {
    this.headerSection.classList.remove(this.classes.headerScrolled);

    if (this.collapseOnScroll) {
      if (!this.navigationManuallyToggled) {
        document.body.classList.remove(this.classes.isHideNav);
        this.headerNavigation.classList.remove(this.classes.hide);
        this.headerNavigation.classList.remove(this.classes.show);
        this.navigationToggleButton.setAttribute('aria-expanded', false);
      }
      this.navigationToggleButton.classList.remove(this.classes.show);
    }

    if (this.isAlwaysSticky) {
      document.body.classList.remove(this.classes.pinned);
    }
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    // this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    // this.addEventListener('mouseleave', this.detectHoverListener.bind(this));

    this.addEventListener('mouseenter', (event) => {
      if (this.hoverLeaveTimer) {
        clearTimeout(this.hoverLeaveTimer);
        this.hoverLeaveTimer = null;
      }
      this.hoverEnterTimer = setTimeout(() => {
        this.detectHover({ type: 'mouseenter' });
      }, 100);
    });

    this.addEventListener('mouseleave', (event) => {
      if (this.hoverEnterTimer) {
        clearTimeout(this.hoverEnterTimer);
        this.hoverEnterTimer = null;
      }

      this.hoverLeaveTimer = setTimeout(() => {
        this.detectHover({ type: 'mouseleave' });
      }, 150);
    });
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    // For touch devices, always use click events
    if (FoxTheme.config.isTouch) {
      return 'click';
    }

    // For non-touch devices, check for custom trigger attribute
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => {
        this.contentElement.setAttribute('open', '');
      }, 100);

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      const MenuProductList = this.querySelector('menu-product-list');
      if (MenuProductList && typeof MenuProductList === 'object') {
        MenuProductList.calcNavButtonsPosition();
      }

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) - 1);

      // Toggle class on body based on lock count
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    // Capture phase on purpose. DetailsDropdown binds its own focusin/click on
    // summaryElement in the bubble phase, so a capture listener on the <details>
    // runs first and the panel is populated before `open` is set — before
    // needsReverse() measures contentElement and before showWithTransition()
    // animates it.
    const hydrate = () => this.hydrateDeferredPanel();
    ['mouseenter', 'focusin', 'click'].forEach((type) => {
      this.addEventListener(type, hydrate, { capture: true });
    });

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.hydrateDeferredPanel();
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  // Clones what mega-menu.liquid parked in a template element. Guarded by a flag
  // rather than by looking for the template every time: this runs on every
  // mouseenter of every top-level menu item, and after the first one there is
  // nothing to find.
  hydrateDeferredPanel() {
    if (this._panelHydrated) return;
    this._panelHydrated = true;

    const template = this.querySelector('template[data-mega-panel]');
    if (!template) return;

    template.replaceWith(template.content.cloneNode(true));
    this.loadCardAssets();
  }

  // A product_list panel draws card-product-xenon, and the header does not emit
  // that card's CSS/JS on every page — see the note in mega-menu.liquid. Fetched
  // here instead, on the panel's first mouseenter / focusin / click, and only
  // for a panel that actually holds cards: a menu with no product list never
  // asks, and neither does a visitor who never points at one.
  //
  // On hover this runs 100ms before `open` (DetailsDropdown's hoverEnterTimer),
  // and the cards only start fading in 350ms after that (theme.css:
  // .mega-menu .promotion-item's transition-delay), so on an ordinary connection
  // the sheet lands before anyone sees a card. On a slow one the list is held
  // back until it has, rather than painting bare full-width images; the rest of
  // the panel opens as normal. visibility rather than display, so Swiper still
  // measures the real column width meanwhile.
  loadCardAssets() {
    const productList = this.querySelector('menu-product-list');
    if (!productList || !productList.querySelector('.xcard')) return;

    productList.style.visibility = 'hidden';
    FoxTheme.utils.loadCardAssets().then(() => {
      productList.style.visibility = '';
      // First measured when the panel finished opening — possibly against the
      // unstyled card, whose photo is a different height.
      if (this.open) productList.calcNavButtonsPosition();
    });
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    const back = this.querySelector('.menu-drawer__item-back-link');
    if (back) {
      back.addEventListener('click', this.onCloseButtonClick.bind(this));
    }
    if (this.getAttribute('level') === 'child') {
      this.addEventListener('toggle', this.onAccordionToggle.bind(this));
      this.summary.addEventListener('click', this.onAccordionSummaryNavigate.bind(this), true);
    }
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('.menu-drawer__item-back-link');
  }

  onAccordionSummaryNavigate(event) {
    if (this.getAttribute('level') !== 'child') return;
    if (!this.open) return;
    const href = this.dataset.parentHref;
    if (!href || href === '#' || href.startsWith('javascript:')) return;
    event.preventDefault();
    event.stopPropagation();
    window.location.assign(href);
  }

  onAccordionToggle() {
    if (this.getAttribute('level') !== 'child') return;
    if (!this.summary) return;
    if (this.open) {
      this.summary.setAttribute('aria-expanded', 'true');
      const li = this.closest('li');
      const ul = li && li.parentElement;
      if (ul && ul.matches('ul')) {
        ul.querySelectorAll(':scope > li > details[is="menu-drawer-details"]').forEach((d) => {
          if (d !== this) d.removeAttribute('open');
        });
      }
    } else {
      this.summary.setAttribute('aria-expanded', 'false');
    }
  }

  onSummaryClick() {
    if (this.getAttribute('level') === 'child') {
      return;
    }
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onCloseButtonClick() {
    if (this.getAttribute('level') === 'child') {
      this.removeAttribute('open');
      if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
      return;
    }
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get header() {
    return this.closest('header');
  }

  // get menuItems() {
  //   if (!this._menuItems) {
  //     this._menuItems = this.querySelectorAll(
  //       '.menu-drawer__menus:not(.active) > ul > li, .menu-drawer__menus:not(.active) > .menu-drawer__footer'
  //     );
  //   }
  //   return this._menuItems;
  // }

  get requiresBodyAppended() {
    return false;
  }

  // Clone anything menu-drawer-details.liquid parked in a <template>: the
  // product_list cards and, since they were the larger half, the two sidebar
  // collection grids. Runs BEFORE super.prepareToShow() so the panel is complete
  // before the drawer animates in and before DrawerComponent.handleAfterShow()
  // calls trapFocus — that helper caches the first and last focusable element at
  // call time, so a card added afterwards would fall outside the tab cycle.
  hydrateDeferredProducts() {
    const templates = this.querySelectorAll('template[data-menu-products]');
    if (!templates.length) return;

    templates.forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
    this.loadCardAssets();

    // Each bar measured an empty grid when it upgraded and switched itself off.
    // disable() first: init() assigns a fresh this.scrollHandler, so calling it
    // twice without disabling leaves the previous listener bound forever.
    this.querySelectorAll('scroll-progress-bar').forEach((bar) => {
      if (typeof bar.init !== 'function') return;
      if (bar.mediaQuery && !bar.mediaQuery.matches) return;
      bar.disable();
      bar.init();
    });
  }

  // The product_list cards are card-product-xenon, whose CSS/JS the header does
  // not emit on every page — see menu-drawer-details.liquid. Fetched on the
  // drawer's first open, and only if one of its panels holds cards. Each list
  // sits a tap further in, in its own submenu, so the sheet is normally in
  // before a list can be seen; if it is not, the list is held back rather than
  // painted as bare full-width images. Same contract as DetailsMega's.
  loadCardAssets() {
    const lists = [...this.querySelectorAll('.menu-drawer__promotions')].filter((list) =>
      list.querySelector('.xcard')
    );
    if (!lists.length) return;

    lists.forEach((list) => (list.style.visibility = 'hidden'));
    FoxTheme.utils.loadCardAssets().then(() => {
      lists.forEach((list) => (list.style.visibility = ''));
    });
  }

  prepareToShow() {
    this.hydrateDeferredProducts();

    super.prepareToShow();
    setTimeout(() => {
      // this.animateMenuItems();
    }, 300);

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  // animateMenuItems() {
  //   FoxTheme.Motion.animate(
  //     this.menuItems,
  //     {
  //       transform: ['translateX(-20px)', 'translateX(0)'],
  //       opacity: [0, 1],
  //     },
  //     {
  //       duration: 0.6,
  //       easing: [0.075, 0.82, 0.165, 1],
  //       delay: FoxTheme.Motion.stagger(0.1),
  //     }
  //   ).finished.then(() => {
  //     this.menuItems.forEach((item) => item.removeAttribute('style'));
  //   });
  // }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is="menu-drawer-details"][level="child"]').forEach((menu) => {
        menu.removeAttribute('open');
        const s = menu.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'false');
      });
      this.querySelectorAll('details[is="menu-drawer-details"][level="top"]').forEach((menu) => {
        if (menu.open) menu.onCloseButtonClick();
      });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  // How many cards the column shows is set by the COLUMN'S width, not by the
  // block's number_of_columns alone. card-product-xenon needs 17rem to hold its
  // instalment chip (see 5c in card-product-xenon.css), and the column is 38-42%
  // of the page, so three columns at 1280px left each card ~132px and the chip
  // ran out of the frame. Each breakpoint adds a card once the column can give
  // every one of them 17rem, up to number_of_columns. `container`, not the
  // window, because the column's share of the page is a theme setting
  // (promotions_width) and the window says nothing about it.
  //
  // 2rem between them, not the old 1rem: a set card's fan leans ~13px into the
  // gap on its right (see the ceiling note on .xcard__fan).
  get slideLayout() {
    const cardMinWidth = 170;
    const gap = 20;
    const breakpoints = {};
    for (let count = 2; count <= this.numberOfColumns; count++) {
      breakpoints[count * cardMinWidth + (count - 1) * gap] = { slidesPerView: count };
    }
    return { spaceBetween: gap, slidesPerView: 1, breakpointsBase: 'container', breakpoints };
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      ...this.slideLayout,
      loop: false,
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;

    const firstMedia = this.querySelector('.xcard__media');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }
}
customElements.define('menu-product-list', MenuProductList);

class MenuSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
  }

  get summarys() {
    return this.querySelectorAll('summary');
  }

  connectedCallback() {
    this.setInitialMinHeight();

    const firstSummary = this.summarys[0];
    if (firstSummary) {
      this.setActiveItem(firstSummary, false);
    }

    this.summarys.forEach((summary) => {
      summary.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.addEventListener('click', (e) => e.preventDefault());
    });

    this.setupIntersectionObserver();
  }

  setInitialMinHeight() {
    const container = this.closest('.mega-menu__wrapper');
    if (container) {
      const parentHeight = container.parentElement.offsetHeight;
      container.style.setProperty('--menu-sidebar-height', `${parentHeight}px`);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateHeight();
        }
      });
    });

    observer.observe(this);
  }

  updateHeight() {
    const activeSummary = this.querySelector('.is-visible');
    if (activeSummary) {
      const container = this.closest('.mega-menu__wrapper');
      const content = activeSummary.nextElementSibling;
      if (container && content) {
        container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
      }
    }
  }

  setActiveItem(summaryElem, updateHeight = true) {
    const lastSidenavElem = this.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }

    summaryElem.classList.add('is-visible');

    if (updateHeight) {
      this.updateHeight();
    }
  }

  handleSidenavMenuToggle(evt) {
    const summaryElem = evt.target;
    this.setActiveItem(summaryElem);
  }

  disconnectedCallback() {
    this.summarys.forEach((summary) => {
      summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.removeEventListener('click', this.handleClick);
    });
  }
}

customElements.define('menu-sidebar', MenuSidebar);
